import { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { LOGO_URL } from '@/lib/utils';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [time, setTime] = useState('');

  useEffect(() => {
    const update = () => setTime(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/work', label: t('nav.work') },
    { to: '/services', label: t('nav.services') },
    { to: '/studio', label: t('nav.studio') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <>
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b border-gold/10 ${scrolled ? 'py-3' : 'py-5'}`}
      style={{ backgroundColor: 'rgba(10,10,11,0.85)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
    >
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex-shrink-0 hover:opacity-80 transition-opacity">
          <img src={LOGO_URL} alt="EB Agency" className="h-9 w-auto transition-transform duration-300 hover:scale-110" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.to.slice(1)}`}
              className={({ isActive }) =>
              `text-sm font-body tracking-wide transition-colors duration-200 relative px-1 py-3 ${isActive ? 'text-gold' : 'text-cream-muted hover:text-cream'}`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && <span className="absolute bottom-2 left-1 right-1 h-px bg-gold" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <span className="text-xs text-cream-muted tracking-wider font-body" data-testid="live-clock">FRANKFURT · {time} CET</span>
          <div className="flex items-center gap-1.5 text-xs font-body" data-testid="lang-switcher">
            <button onClick={() => i18n.changeLanguage('de')} className={`transition-colors ${i18n.language?.startsWith('de') ? 'text-gold' : 'text-cream-muted hover:text-cream'}`}>DE</button>
            <span className="text-smoke">/</span>
            <button onClick={() => i18n.changeLanguage('en')} className={`transition-colors ${i18n.language?.startsWith('en') ? 'text-gold' : 'text-cream-muted hover:text-cream'}`}>EN</button>
          </div>
          <Link
            to="/contact"
            data-testid="header-start-project"
            className="px-5 py-2.5 min-w-[180px] inline-flex items-center justify-center text-center text-xs uppercase tracking-[0.15em] font-body border border-gold/60 text-gold hover:bg-gold hover:text-obsidian transition-all duration-300"
          >
            {t('nav.startProject')}
          </Link>
        </div>

        <button className="md:hidden text-cream p-1" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>

    {/* Mobile-Menü bewusst als Sibling des <header> gerendert: der Header hat
        backdrop-filter, was in den meisten Browsern einen Containing Block für
        position:fixed-Children erzeugt. Innerhalb des Headers würde `inset-0`
        deshalb nur die Header-Höhe abdecken statt das Viewport. */}
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden fixed inset-0 bg-obsidian z-[60] flex flex-col items-center pt-24 pb-10 px-6 overflow-y-auto"
          data-testid="mobile-menu"
        >
          <button className="absolute top-5 right-6 text-cream p-1" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <X size={28} />
          </button>

          <div className="my-auto flex flex-col items-center gap-7">
            <img src={LOGO_URL} alt="EB Agency" className="h-10 w-auto mb-4" />
            {navLinks.map((link, i) => (
              <motion.div key={link.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <NavLink
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `text-3xl font-display tracking-tight ${isActive ? 'text-gold' : 'text-cream'}`}
                >
                  {link.label}
                </NavLink>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-5">
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="px-6 py-3 text-sm uppercase tracking-[0.15em] font-body border border-gold/60 text-gold"
            >
              {t('nav.startProject')}
            </Link>
            <div className="flex items-center gap-3 text-sm">
              <button onClick={() => i18n.changeLanguage('de')} className={i18n.language?.startsWith('de') ? 'text-gold' : 'text-cream-muted'}>DE</button>
              <span className="text-smoke">/</span>
              <button onClick={() => i18n.changeLanguage('en')} className={i18n.language?.startsWith('en') ? 'text-gold' : 'text-cream-muted'}>EN</button>
            </div>
            <span className="text-xs text-cream-muted">{time} CET</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
